import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import PageHeader from '../components/PageHeader.jsx';

import Loader, { ErrorBox } from '../components/Loader.jsx';
import { ProfileAPI } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useNotify } from '../lib/notifications';
import { STORAGE_KEYS } from '../config';

/**
 * Self-service profile page — "My Profile".
 *
 * Reachable from:
 *   * the user's name / avatar in the AppShell top-right corner,
 *   * the deep link ``/me`` (see ``ROUTES.PROFILE`` in ``config.js``).
 *
 * What the user can edit
 * ----------------------
 *   * Name (first / last)
 *   * Username
 *   * Email
 *   * Phone number
 *   * City / State / Address
 *   * Profile picture (DP)
 *   * Password (current + new, confirmed twice)
 *
 * What the user CANNOT edit here
 * ------------------------------
 * Privilege flags (``is_admin``, ``is_staff``, ``is_superuser``),
 * ``role``, ``organisation``, ``division``, ``hierarchy`` etc. are
 * deliberately not exposed.  The backend's ``UserViewSet.update_me``
 * action whitelists only the safe identity fields (see
 * ``super_admin/api_views.py``), so even a malicious request that tries
 * to PATCH them is silently dropped server-side — defence in depth.
 *
 * Data flow
 * ---------
 *   1. Initial state seeds from ``useAuth().user`` (already in memory
 *      from the bootstrap ``/me/`` call).
 *   2. On save, we POST the (possibly multipart, when a new DP is
 *      attached) form to ``/api/super_admin/users/me/update/``.
 *   3. The backend returns a fresh ``/me/`` payload — we push it into
 *      ``setUser`` AND ``localStorage`` so the avatar / name in the
 *      top bar refresh immediately without a full page reload.
 */
export default function ProfilePage() {
  const { user, setUser } = useAuth();
  const notify = useNotify();
  const location = useLocation();


  // ---- profile form state ------------------------------------------------
  // Seed from the in-memory user immediately so the form isn't blank
  // for a frame.  We still re-fetch on mount to catch any drift from
  // another tab or admin edit since we last opened the SPA.
  const [form, setForm] = useState(() => seedFormFromUser(user));
  const [picPreview, setPicPreview] = useState(
    user?.profile_image_url || '',
  );
  const fileRef = useRef(null);
  const pickedFile = useRef(null);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // ---- password change state --------------------------------------------
  const [pwd, setPwd] = useState({ current: '', next: '', confirm: '' });
  const [pwdSaving, setPwdSaving] = useState(false);
  const [pwdError, setPwdError] = useState(null);
  const pwdCardRef = useRef(null);

  // Deep-link support for ``/me#password`` — the "Change Password" entry
  // in the top-right user dropdown (``components/UserMenu.jsx``) routes
  // here with that hash.  Because this page renders BOTH the identity
  // card and the password card, we scroll the password card into view
  // and focus its first input so the user lands exactly where they
  // expected instead of at the top of a long form.
  //
  // Re-runs on ``location.key`` (not just ``hash``) so picking the menu
  // item again while already on /me still re-scrolls.
  useEffect(() => {
    if (location.hash !== '#password') return undefined;
    // Defer a frame so the cards are laid out before we measure.
    const id = window.requestAnimationFrame(() => {
      pwdCardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      document.getElementById('pf-pwd-current')?.focus({ preventScroll: true });
    });
    return () => window.cancelAnimationFrame(id);
  }, [location.hash, location.key]);


  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    ProfileAPI.retrieve()
      .then((me) => {
        if (cancelled) return;
        setForm(seedFormFromUser(me));
        setPicPreview(me?.profile_image_url || '');
        // Refresh the in-memory + localStorage user too so the rest
        // of the SPA (top bar avatar, etc.) stays in sync.
        setUser(me);
        try {
          localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(me));
        } catch (_) { /* quota / private mode — non-fatal */ }
      })
      .catch((e) => { if (!cancelled) setError(e); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // ``setUser`` is stable across renders (useCallback in AuthProvider)
    // so we're safe to omit it from deps.  ESLint is not aware of that
    // contract, hence the disable below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---------------------------------------------------------------- helpers
  const onField = (key) => (e) => {
    const value = e?.target ? e.target.value : e;
    setForm((f) => ({ ...f, [key]: value }));
  };

  const onPickFile = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) {
      pickedFile.current = null;
      return;
    }
    // Reasonable client-side guard so we don't try to upload, say, a
    // 50MB photo.  The backend's ImageField will also reject anything
    // it can't decode, so this is purely UX.
    if (file.size > 5 * 1024 * 1024) {
      notify.error('Profile picture must be under 5 MB.', { title: 'Image too large' });
      e.target.value = '';
      return;
    }
    pickedFile.current = file;
    // Local object URL for the preview — revoked on next pick / unmount.
    const url = URL.createObjectURL(file);
    setPicPreview((prev) => {
      if (prev && prev.startsWith('blob:')) URL.revokeObjectURL(prev);
      return url;
    });
  };

  // Revoke any preview blob URL on unmount.
  useEffect(() => {
    return () => {
      setPicPreview((prev) => {
        if (prev && prev.startsWith('blob:')) URL.revokeObjectURL(prev);
        return '';
      });
    };
  }, []);

  // ---------------------------------------------------------------- submit
  const onSaveProfile = async (e) => {
    e?.preventDefault?.();
    setError(null);
    setSaving(true);
    try {
      // Always send multipart so a freshly picked picture goes up in
      // the same round-trip as the rest of the fields.  When the user
      // didn't pick a new picture we omit the field entirely (the
      // backend treats empty strings as "leave existing image alone"
      // — see ``UserViewSet.update_me``).
      const fd = new FormData();
      const append = (k, v) => {
        if (v === undefined || v === null) return;
        fd.append(k, v);
      };
      append('first_name',   form.first_name || '');
      append('last_name',    form.last_name || '');
      append('username',     form.username || '');
      append('email',        form.email || '');
      append('phone_number', form.phone_number || '');
      append('city',         form.city || '');
      append('state',        form.state || '');
      append('address',      form.address || '');
      if (pickedFile.current) {
        fd.append('profile_image', pickedFile.current);
      }

      const updated = await ProfileAPI.update(fd);

      // Push into the auth context + localStorage so the top-bar
      // avatar / name reflect the change without a reload.
      setUser(updated);
      try {
        localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(updated));
      } catch (_) { /* non-fatal */ }
      pickedFile.current = null;
      if (fileRef.current) fileRef.current.value = '';
      // Update the preview to point at the freshly-saved server URL —
      // also frees the blob URL we were using for the local preview.
      setPicPreview((prev) => {
        if (prev && prev.startsWith('blob:')) URL.revokeObjectURL(prev);
        return updated?.profile_image_url || '';
      });

      notify.success('Profile updated.', { title: 'Saved' });
    } catch (e) {
      setError(e);
    } finally {
      setSaving(false);
    }
  };

  const onChangePassword = async (e) => {
    e?.preventDefault?.();
    setPwdError(null);
    if (!pwd.current) {
      setPwdError('Please enter your current password.');
      return;
    }
    if (!pwd.next || pwd.next.length < 4) {
      setPwdError('New password must be at least 4 characters.');
      return;
    }
    if (pwd.next !== pwd.confirm) {
      setPwdError('New password and confirmation do not match.');
      return;
    }
    setPwdSaving(true);
    try {
      await ProfileAPI.setPassword(pwd.current, pwd.next);
      setPwd({ current: '', next: '', confirm: '' });
      notify.success('Password changed successfully.', { title: 'Saved' });
    } catch (e) {
      setPwdError(e);
    } finally {
      setPwdSaving(false);
    }
  };

  // ---------------------------------------------------------------- render
  const initial = useMemo(() => {
    const src = (form.first_name || form.email || user?.email || '?')
      .toString().trim();
    return src.charAt(0).toUpperCase();
  }, [form.first_name, form.email, user]);

  if (loading && !user) {
    return <Loader label="Loading profile…" />;
  }

  return (
    <div>
      <PageHeader
        title="My Profile"
        subtitle="Update your name, contact details, profile picture and password."
      />

      <ErrorBox error={error} />

      <div
        style={{
          display: 'grid',
          gap: 16,
          gridTemplateColumns: 'minmax(0, 1fr)',
        }}
      >
        {/* ------------------ identity card ------------------ */}
        <form onSubmit={onSaveProfile} className="ty-card" style={{ padding: 20 }}>
          <h3 style={{ marginTop: 0, marginBottom: 16 }}>Personal details</h3>

          {/* Avatar + file picker -------------------------------------- */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 16,
              marginBottom: 18,
              flexWrap: 'wrap',
            }}
          >
            <div
              style={{
                width: 96,
                height: 96,
                borderRadius: '50%',
                overflow: 'hidden',
                display: 'grid',
                placeItems: 'center',
                color: '#fff',
                fontSize: 36,
                fontWeight: 700,
                background: 'linear-gradient(135deg, #7980ff 0%, #656cfd 100%)',
                boxShadow: '0 6px 14px rgba(121, 128, 255, 0.35)',
                flex: '0 0 auto',
              }}
            >
              {picPreview ? (
                // eslint-disable-next-line jsx-a11y/img-redundant-alt
                <img
                  src={picPreview}
                  alt="Profile picture"
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              ) : (
                <span>{initial}</span>
              )}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <label className="ty-btn ty-btn--secondary" style={{ cursor: 'pointer' }}>
                Choose new picture
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  onChange={onPickFile}
                  style={{ display: 'none' }}
                />
              </label>
              <span style={{ fontSize: 12, color: 'var(--ty-muted)' }}>
                PNG / JPG, up to 5 MB. Square images look best.
              </span>
            </div>
          </div>

          <div className="ty-grid-2">
            <div className="ty-field">
              <label htmlFor="pf-first">First name</label>
              <input
                id="pf-first"
                className="ty-input"
                value={form.first_name}
                onChange={onField('first_name')}
                autoComplete="given-name"
              />
            </div>
            <div className="ty-field">
              <label htmlFor="pf-last">Last name</label>
              <input
                id="pf-last"
                className="ty-input"
                value={form.last_name}
                onChange={onField('last_name')}
                autoComplete="family-name"
              />
            </div>

            <div className="ty-field">
              <label htmlFor="pf-username">Username</label>
              <input
                id="pf-username"
                className="ty-input"
                value={form.username}
                onChange={onField('username')}
                autoComplete="username"
              />
            </div>
            <div className="ty-field">
              <label htmlFor="pf-email">Email</label>
              <input
                id="pf-email"
                type="email"
                className="ty-input"
                value={form.email}
                onChange={onField('email')}
                autoComplete="email"
              />
            </div>

            <div className="ty-field">
              <label htmlFor="pf-phone">Phone number</label>
              <input
                id="pf-phone"
                className="ty-input"
                value={form.phone_number}
                onChange={onField('phone_number')}
                autoComplete="tel"
              />
            </div>
            <div className="ty-field">
              <label htmlFor="pf-city">City</label>
              <input
                id="pf-city"
                className="ty-input"
                value={form.city}
                onChange={onField('city')}
                autoComplete="address-level2"
              />
            </div>

            <div className="ty-field">
              <label htmlFor="pf-state">State</label>
              <input
                id="pf-state"
                className="ty-input"
                value={form.state}
                onChange={onField('state')}
                autoComplete="address-level1"
              />
            </div>
            <div className="ty-field">
              <label htmlFor="pf-address">Address</label>
              <textarea
                id="pf-address"
                className="ty-textarea"
                rows={2}
                value={form.address}
                onChange={onField('address')}
                autoComplete="street-address"
              />
            </div>
          </div>

          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
            <button
              type="submit"
              className="ty-btn"
              disabled={saving}
            >
              {saving ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        </form>

        {/* ------------------ password card ------------------
            ``id="password"`` + the ref power the ``/me#password``
            deep link used by the top-right user dropdown. */}
        <form
          id="password"
          ref={pwdCardRef}
          onSubmit={onChangePassword}
          className="ty-card"
          style={{ padding: 20, scrollMarginTop: 16 }}
        >
          <h3 style={{ marginTop: 0, marginBottom: 16 }}>Change password</h3>

          <ErrorBox error={pwdError} />
          <div className="ty-grid-2">
            <div className="ty-field">
              <label htmlFor="pf-pwd-current">Current password</label>
              <input
                id="pf-pwd-current"
                type="password"
                className="ty-input"
                value={pwd.current}
                onChange={(e) => setPwd((p) => ({ ...p, current: e.target.value }))}
                autoComplete="current-password"
              />
            </div>
            <div /* spacer to keep grid alignment on wide screens */ />
            <div className="ty-field">
              <label htmlFor="pf-pwd-new">New password</label>
              <input
                id="pf-pwd-new"
                type="password"
                className="ty-input"
                value={pwd.next}
                onChange={(e) => setPwd((p) => ({ ...p, next: e.target.value }))}
                autoComplete="new-password"
              />
            </div>
            <div className="ty-field">
              <label htmlFor="pf-pwd-confirm">Confirm new password</label>
              <input
                id="pf-pwd-confirm"
                type="password"
                className="ty-input"
                value={pwd.confirm}
                onChange={(e) => setPwd((p) => ({ ...p, confirm: e.target.value }))}
                autoComplete="new-password"
              />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
            <button
              type="submit"
              className="ty-btn ty-btn--secondary"
              disabled={pwdSaving}
            >
              {pwdSaving ? 'Updating…' : 'Update password'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/**
 * Map a ``/me/`` payload to the form's editable subset.  Keep this in
 * lock-step with the whitelist in ``UserViewSet.update_me`` — every
 * field listed here MUST also be in that whitelist or saves will
 * silently drop it.
 */
function seedFormFromUser(u) {
  return {
    first_name:   u?.first_name || '',
    last_name:    u?.last_name || '',
    username:     u?.username || '',
    email:        u?.email || '',
    phone_number: u?.phone_number || '',
    city:         u?.city || '',
    state:        u?.state || '',
    address:      u?.address || '',
  };
}
